<script setup lang="ts">
import { computed, ref, toRefs, watch } from '#imports';
import { useElementSize, useEventListener } from '@vueuse/core';
import { arrow as arrowMiddleware, offset, autoPlacement, useFloating, autoUpdate, shift, type Placement } from '@floating-ui/vue';
import { UiPopperPlacement as UiPlacement } from '../popper/PopperPlacement';
import { activeTooltip } from './tooltipState';

const props = withDefaults(defineProps<{
  arrow?: boolean;
  text?: string;
  delayEnter?: number;
  delayLeave?: number;
  minWidth?: string;
  reference?: HTMLElement;
  placement?: Placement;
}>(), {
  arrow: true,
  text: '',
  delayEnter: 100,
  delayLeave: 100,
  minWidth: 'auto',
  placement: UiPlacement.Bottom,
});

const isHovered = ref(false);

const floating = ref();
const floatingArrow = ref();

const { reference } = toRefs(props);

watch(isHovered, (v) => {
  if (v) {
    activeTooltip.value = reference.value;
  }
});

watch(activeTooltip, () => {
  if (isHovered.value && activeTooltip.value !== reference.value) isHovered.value = false;
});

const { floatingStyles, middlewareData } = useFloating(reference, floating, {
  placement: props.placement,
  open: isHovered,
  whileElementsMounted: autoUpdate,
  middleware: [
    offset(8),
    shift(),
    autoPlacement({
      padding: 8,
      // allowedPlacements: props.placements,
    }),
    arrowMiddleware({element: floatingArrow, padding: 12 }),
  ],
});

const bottomArrow = { bottom: '-5px', transform: 'rotate(0deg)' };
const topArrow = { top: '-5px', transform: 'rotate(180deg)' };
const leftArrow = { left: '-7px', transform: 'rotate(90deg)' };
const rightArrow = { right: '-7px', transform: 'rotate(-90deg)' };

const arrowStyles = computed<CSSStyleValue>(() => {
  const arrow = middlewareData.value.arrow || { x: 0, y: 0 };
  const placement = middlewareData.value.offset?.placement;
  if (arrow.x != null) {
    return {
      left: `${arrow.x || 0}px`,
      ...(placement === UiPlacement.Top ? bottomArrow : topArrow),
    };
  }
  
  return {
    top: `${arrow.y || 0}px`,
    ...(placement === UiPlacement.Right ? leftArrow : rightArrow),
  };
});

const { width } = useElementSize(reference);

const popperWidth = computed(() => {
  if (props.minWidth) return props.minWidth;
  if (width.value) {
    return `${width.value}px`;
  }
  return '100px';
});

let timer: ReturnType<typeof setTimeout> | undefined;

function toggle(entering: boolean) {
  const delay = entering ? props.delayEnter : props.delayLeave;
  if (timer) {
    clearTimeout(timer);
    timer = undefined;
  }
  
  if (delay)
    timer = setTimeout(() => isHovered.value = entering, delay);
  else
    isHovered.value = entering;
}

function showTooltip() {
  toggle(true);
}

function hideTooltip() {
  toggle(false);
}

useEventListener(reference, 'mouseenter', showTooltip);
useEventListener(reference, 'mouseleave', hideTooltip);

defineExpose({
  showTooltip,
  hideTooltip,
});
</script>

<template lang="pug">
Transition(name="tooltip")
  .ui-tooltip.br-8.z-index-3000.max-w-200(
    v-if="isHovered"
    ref="floating"
    :style="{...floatingStyles, 'min-width': popperWidth}"
    @mouseover="showTooltip"
    @mouseleave="hideTooltip"
    @click="hideTooltip"
  )
    .ui-tooltip-text.typ-12-16-400.p-4-8 {{ text }}
    .ui-tooltip-arrow.w-0.h-0(v-if="arrow" ref="floatingArrow" :style="arrowStyles")
</template>

<style scoped lang="scss">
@import '../../styles/colors';

.ui-tooltip {
  transition: background 250ms ease-in-out;
  background: $ui-color-bg-dark;
}

.ui-tooltip-text {
  color: $ui-color-text-white;
}

.ui-tooltip-arrow {
  position: absolute;
  transform: rotate(180deg);
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid $ui-color-bg-dark;
}

.tooltip-enter-active,
.tooltip-leave-active {
  transition: opacity 0.2s ease;
}

.tooltip-enter,
.tooltip-leave-to {
  opacity: 0;
}
</style>